/*
 * ============================================================================
 * TableSearchInput — barre de recherche standard des tables iSAL
 * ----------------------------------------------------------------------------
 * POSITIONNEMENT DANS UNE TABLE :
 *   Se place dans la barre d'outils du conteneur Paper de la table
 *   (MuiPaper-elevation2), cote DROIT. Convention iSAL :
 *     - la recherche est alignee a droite, AU-DESSUS du bouton
 *       ExportMenuButton (Stack direction="column", alignItems="flex-end")
 *     - a gauche de la toolbar : filtres Select (stack vertical) ou titre
 *       + compteur de lignes
 *   Layout type :
 *     <Stack direction="row" justifyContent="space-between">
 *         <Stack direction="column">  <filtres />        </Stack>
 *         <Stack direction="column" alignItems="flex-end">
 *             <TableSearchInput />          <- en haut a droite
 *             <count /> + <ExportMenuButton />  <- en dessous
 *         </Stack>
 *     </Stack>
 *
 * DESIGN UI :
 *   - Input antd size="small" (par defaut), icone SearchOutlined en prefixe
 *   - allowClear : croix integree pour vider le champ
 *   - placeholder "Rechercher...", largeur 220px par defaut (prop width)
 *
 * USAGE :
 *   const [search, setSearch] = useState('');
 *   <TableSearchInput value={search} onChange={setSearch} />
 *
 *   Recherche locale : filtrer les lignes en memoire (voir
 *   JournalFinancement/Tab1.tsx).
 *   Recherche serveur : debouncer le changement (~400ms), remettre la page a
 *   1 puis refetch avec le texte en parametre — voir
 *   GestionActifs/Demande/Tab2.tsx.
 * ============================================================================
 */
import React from 'react';
import { Input } from 'antd';
import { Box } from '@mui/material';
import { SearchOutlined } from '@ant-design/icons';

// Meme bleu que l'en-tete des tables antd (var(--primaryColor))
const CSAL_BLUE = 'var(--primaryColor, #116599)';

export interface TableSearchInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    width?: number;
    size?: 'small' | 'middle' | 'large';
    // blueIcon : variante "bloc icone bleu" a gauche du champ
    // (addonBefore colore) — alignee sur TableFilterSelect
    blueIcon?: boolean;
}

const TableSearchInput: React.FC<TableSearchInputProps> = ({
    value,
    onChange,
    placeholder = 'Rechercher...',
    width = 220,
    size = 'small',
    blueIcon = false
}) => {
    if (blueIcon) {
        return (
            <Box sx={{
                display: 'inline-flex',
                width,
                '& .ant-input-group-wrapper': { width: '100%' },
                '& .ant-input-group-addon': {
                    bgcolor: CSAL_BLUE,
                    color: '#fff',
                    border: `1px solid ${CSAL_BLUE}`,
                    px: 1,
                    borderTopLeftRadius: 6,
                    borderBottomLeftRadius: 6
                },
                '& .ant-input-affix-wrapper': {
                    borderColor: CSAL_BLUE,
                    borderTopRightRadius: 6,
                    borderBottomRightRadius: 6,
                    '&.ant-input-affix-wrapper-focused, &:hover': { borderColor: CSAL_BLUE }
                }
            }}>
                <Input
                    size={size}
                    allowClear
                    addonBefore={<SearchOutlined />}
                    placeholder={placeholder}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Box>
        );
    }
    return (
        <Input
            size={size}
            allowClear
            prefix={<SearchOutlined />}
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ width }}
        />
    );
};

export default TableSearchInput;
